import { useRef } from 'react';
import { authenticatedPost } from '../../lib/api/authenticated-fetch.js';
import { generateClientChatId } from '../../lib/utils/client-chat-id.js';
import { logger } from '../../lib/logger.js';

interface MessageHandlersProps {
  apiBaseUrl: string;
  onError: (error: string) => void;
  onSuccess?: () => void;
}

/**
 * Message handlers with consolidated error handling
 */
export function useMessageHandlers({ apiBaseUrl, onError, onSuccess }: MessageHandlersProps) {
  // See useChatHandlers: this "start a chat from the first query" path is the
  // lazy create used when the user sends the first message. It also creates the
  // chat row BEFORE the message is attached, so a duplicate invocation (send
  // double-click, StrictMode double-invoke, re-render) previously left orphan
  // empty rows. Collapse concurrent calls onto one in-flight request and send a
  // client-minted stable chatId so the server's idempotent create-by-id
  // collapses any retry onto the same row.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const startChatWithQueryHandler = async (
    query: string,
    onChatCreated: (chatId: string) => void,
    onInitialQuerySet: (query: string) => void,
    onChatsReload: () => Promise<void>,
    onLoadingChange: (isLoading: boolean) => void,
    options?: {
      /**
       * Chat id reserved by the caller (the start-screen composer mints one so
       * attachments can be uploaded before the row exists). The server create is
       * idempotent by id, so reusing it is safe.
       */
      clientChatId?: string;
      /** Allow a files-only first message (no text). */
      allowEmptyQuery?: boolean;
    }
  ) => {
    if (!query.trim() && !options?.allowEmptyQuery) return;
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const run = (async () => {
      try {
        onLoadingChange(true);
        logger.debug('starting chat with query', { query, apiBaseUrl });

        // Don't pass a title - let the backend auto-generate it from the first
        // message. A client-minted chatId makes the create idempotent per intent.
        const data = await authenticatedPost<any>(`${apiBaseUrl}/chats`, {
          chatId: options?.clientChatId ?? generateClientChatId(),
        });
        logger.debug('chat creation response', { data });

        if (data.success) {
          // Update URL to the new chat (this will trigger the sync effect)
          onChatCreated(data.data.chatId);
          onInitialQuerySet(query);
          await onChatsReload();
          onSuccess?.();
        } else {
          onError(data.error || 'Failed to create chat');
        }
      } catch (err) {
        logger.error('chat creation error', err);
        onError(err instanceof Error ? err.message : 'Failed to create chat');
      } finally {
        onLoadingChange(false);
      }
    })();

    inFlightRef.current = run;
    try {
      await run;
    } finally {
      inFlightRef.current = null;
    }
  };

  return { startChatWithQueryHandler };
}
